// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useState } from 'react';
import { Button, Checkbox, IconButton, Select, TextArea, TextInput } from '@loa/ui-components';
import type { BindingCatalogue } from './types';

/**
 * No-code builder for a New Business template's parameter schema (DSL-v2). Emits
 * the same JSON the raw editor produced — {@code { version, sections[], questions[] }}
 * — so the client/advisor wizard (SchemaFactFindForm) renders it: collapsible
 * sections, a 2-column field grid, full-width fields, required markers.
 *
 * Each field's {@code id} is the binding key the PDF overlay references as
 * {@code param.<id>}, so it's editable + shown explicitly.
 */

export interface SchemaField {
  id: string;
  label?: string;
  type: string;
  required?: boolean;
  width?: 'half' | 'full';
  options?: string[];
  section?: string;
  /** Constant default value (prefills the field when no reference resolves). */
  default?: string;
  /** Reference default — a binding data key (e.g. client.firstName) resolved at issue time. */
  prefillFrom?: string;
  /** Display-only: shown to the client from its default/reference value, never editable. */
  readOnly?: boolean;
}
export interface SchemaSection {
  id: string;
  title: string;
  /** Optional headline shown under the section title. */
  description?: string;
}
export interface ParamSchema {
  version?: string | number;
  title?: string;
  sections?: SchemaSection[];
  questions?: SchemaField[];
}

interface Props {
  schema: Record<string, unknown>;
  onChange: (schema: Record<string, unknown>) => void;
  /**
   * The binding catalogue (Client / Advisor / Brokerage / Product / …) used to
   * offer a field's "From system data" default. Optional — when absent the
   * reference-default mode is hidden and only a constant default is offered.
   */
  catalogue?: BindingCatalogue | null;
}

type DefaultMode = 'none' | 'constant' | 'reference';
const DEFAULT_MODES: { value: DefaultMode; text: string }[] = [
  { value: 'none', text: 'No default' },
  { value: 'constant', text: 'Constant value' },
  { value: 'reference', text: 'From system data' },
];

/**
 * A field's current default mode, derived from which annotation it carries.
 * Presence (not truthiness) decides — a just-selected reference/constant starts
 * empty, so an empty string still means the author chose that mode.
 */
function defaultMode(field: SchemaField): DefaultMode {
  if (field.prefillFrom != null) return 'reference';
  if (field.default != null) return 'constant';
  return 'none';
}

/** Reference options from the catalogue, excluding the `param.*` convention row. */
function referenceOptions(catalogue?: BindingCatalogue | null): { value: string; text: string }[] {
  const opts: { value: string; text: string }[] = [{ value: '', text: 'Select a system field…' }];
  for (const group of catalogue?.groups ?? []) {
    for (const entry of group.entries ?? []) {
      if (entry.key.startsWith('param.')) continue;
      opts.push({ value: entry.key, text: `${group.name} · ${entry.label}` });
    }
  }
  return opts;
}

/** Field types offered in the builder (a curated subset of the DSL union). */
const FIELD_TYPES: { value: string; text: string }[] = [
  { value: 'string', text: 'Short text' },
  { value: 'text', text: 'Long text' },
  { value: 'number', text: 'Number' },
  { value: 'money', text: 'Money (€)' },
  { value: 'percent', text: 'Percent (%)' },
  { value: 'date', text: 'Date' },
  { value: 'boolean', text: 'Yes / No' },
  { value: 'checkbox', text: 'Checkbox' },
  { value: 'enum', text: 'Dropdown (single)' },
  { value: 'radio', text: 'Radio buttons (single)' },
  { value: 'multiselect', text: 'Dropdown (multi)' },
  { value: 'phone', text: 'Phone' },
  { value: 'country', text: 'Country' },
];
const WIDTHS = [
  { value: 'half', text: 'Half width' },
  { value: 'full', text: 'Full width' },
];
const NEEDS_OPTIONS = new Set(['enum', 'radio', 'multiselect']);

const slug = (s: string) =>
  s.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'field';

function uniqueId(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}_${i}`)) i += 1;
  return `${base}_${i}`;
}

export default function NewBusinessParamSchemaBuilder({ schema, onChange, catalogue }: Props) {
  const sections: SchemaSection[] = Array.isArray(schema.sections) ? (schema.sections as SchemaSection[]) : [];
  const questions: SchemaField[] = Array.isArray(schema.questions) ? (schema.questions as SchemaField[]) : [];

  // Local draft buffers for the two free-text inputs whose committed value differs
  // from what the user types: the binding id (slugified + de-duped on blur, so the
  // React key stays stable while typing → no focus loss) and the options textarea
  // (blank lines kept while typing → Enter inserts a newline, cleaned on blur).
  const [idDrafts, setIdDrafts] = useState<Record<string, string>>({});
  const [optionDrafts, setOptionDrafts] = useState<Record<string, string>>({});
  const clearDraft = (setter: typeof setIdDrafts, key: string) =>
    setter((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });

  const emit = (nextSections: SchemaSection[], nextQuestions: SchemaField[]) =>
    onChange({
      ...schema,
      version: schema.version ?? '1',
      sections: nextSections,
      questions: nextQuestions,
    });

  const allIds = () => new Set<string>([...sections.map((s) => s.id), ...questions.map((q) => q.id)]);
  const fieldsOf = (sectionId: string) => questions.filter((q) => (q.section ?? sections[0]?.id) === sectionId);

  const addSection = () => {
    const id = uniqueId('section', allIds());
    emit([...sections, { id, title: `Part ${sections.length + 1}` }], questions);
  };
  const renameSection = (id: string, title: string) =>
    emit(sections.map((s) => (s.id === id ? { ...s, title } : s)), questions);
  const setSectionDescription = (id: string, description: string) =>
    emit(sections.map((s) => (s.id === id ? { ...s, description } : s)), questions);
  const removeSection = (id: string) =>
    emit(sections.filter((s) => s.id !== id), questions.filter((q) => (q.section ?? sections[0]?.id) !== id));
  const moveSection = (id: string, dir: -1 | 1) => {
    const i = sections.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= sections.length) return;
    const next = [...sections];
    [next[i], next[j]] = [next[j], next[i]];
    emit(next, questions);
  };

  const addField = (sectionId: string) => {
    const id = uniqueId('field', allIds());
    emit(sections, [
      ...questions,
      { id, section: sectionId, label: 'New field', type: 'string', required: false, width: 'half' },
    ]);
  };
  const updateField = (id: string, patch: Partial<SchemaField>) =>
    emit(sections, questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  const changeFieldId = (oldId: string, raw: string) => {
    const next = uniqueId(slug(raw), new Set([...allIds()].filter((x) => x !== oldId)));
    emit(sections, questions.map((q) => (q.id === oldId ? { ...q, id: next } : q)));
  };
  const setDefaultMode = (field: SchemaField, mode: DefaultMode) => {
    if (mode === 'none') updateField(field.id, { default: undefined, prefillFrom: undefined });
    else if (mode === 'constant') updateField(field.id, { prefillFrom: undefined, default: field.default ?? '' });
    else updateField(field.id, { default: undefined, prefillFrom: field.prefillFrom ?? '' });
  };
  const removeField = (id: string) => emit(sections, questions.filter((q) => q.id !== id));
  const moveField = (id: string, dir: -1 | 1) => {
    const sectionId = questions.find((q) => q.id === id)?.section ?? sections[0]?.id;
    const siblings = fieldsOf(sectionId ?? '');
    const pos = siblings.findIndex((q) => q.id === id);
    const swapWith = siblings[pos + dir];
    if (!swapWith) return;
    const a = questions.findIndex((q) => q.id === id);
    const b = questions.findIndex((q) => q.id === swapWith.id);
    const next = [...questions];
    [next[a], next[b]] = [next[b], next[a]];
    emit(sections, next);
  };

  return (
    <div data-testid="nb-param-schema-builder">
      {sections.length === 0 && (
        <p style={{ color: 'var(--color-secondary-text)', fontSize: 'var(--font-size-body-03)', margin: '0 0 var(--spacing-sm)' }}>
          No sections yet. Add a section to start collecting extra information on the application form.
        </p>
      )}

      {sections.map((section, sIdx) => (
        <div
          key={section.id}
          data-testid={`nbsb-section-${section.id}`}
          style={{
            border: '1px solid var(--color-grey-04)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--spacing-md)',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <TextInput
                fluid
                label="Section title"
                value={section.title}
                onChange={(e) => renameSection(section.id, e.target.value)}
                data-testid={`nbsb-section-title-${section.id}`}
              />
            </div>
            <IconButton variant="square-outlined" size="sm" aria-label="Move section up"
              disabled={sIdx === 0} onClick={() => moveSection(section.id, -1)} icon={<span>↑</span>} />
            <IconButton variant="square-outlined" size="sm" aria-label="Move section down"
              disabled={sIdx === sections.length - 1} onClick={() => moveSection(section.id, 1)} icon={<span>↓</span>} />
            <IconButton variant="square-outlined" size="sm" aria-label="Remove section"
              onClick={() => removeSection(section.id)} data-testid={`nbsb-remove-section-${section.id}`} icon={<span>✕</span>} />
          </div>

          <div style={{ marginTop: 'var(--spacing-sm)' }}>
            <TextInput
              fluid
              label="Section headline (optional)"
              value={section.description ?? ''}
              onChange={(e) => setSectionDescription(section.id, e.target.value)}
              data-testid={`nbsb-section-description-${section.id}`}
            />
          </div>

          <div style={{ marginTop: 'var(--spacing-md)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
            {fieldsOf(section.id).map((field, fIdx, arr) => (
              <div
                key={field.id}
                data-testid={`nbsb-field-${field.id}`}
                style={{
                  border: '1px solid var(--color-grey-03)',
                  borderRadius: 'var(--radius-input)',
                  padding: 'var(--spacing-sm)',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 'var(--spacing-sm)',
                }}
              >
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 2, minWidth: 160 }}>
                    <TextInput fluid label="Label" value={field.label ?? ''}
                      onChange={(e) => updateField(field.id, { label: e.target.value })}
                      data-testid={`nbsb-field-label-${field.id}`} />
                  </div>
                  <div style={{ flex: 1, minWidth: 120 }}>
                    <TextInput fluid label="Binding id (param.)"
                      value={idDrafts[field.id] ?? field.id}
                      onChange={(e) => setIdDrafts((d) => ({ ...d, [field.id]: e.target.value }))}
                      onBlur={() => {
                        const draft = idDrafts[field.id];
                        clearDraft(setIdDrafts, field.id);
                        if (draft !== undefined && slug(draft) !== field.id) changeFieldId(field.id, draft);
                      }}
                      data-testid={`nbsb-field-id-${field.id}`} />
                  </div>
                  <IconButton variant="square-outlined" size="sm" aria-label="Move field up"
                    disabled={fIdx === 0} onClick={() => moveField(field.id, -1)} icon={<span>↑</span>} />
                  <IconButton variant="square-outlined" size="sm" aria-label="Move field down"
                    disabled={fIdx === arr.length - 1} onClick={() => moveField(field.id, 1)} icon={<span>↓</span>} />
                  <IconButton variant="square-outlined" size="sm" aria-label="Remove field"
                    onClick={() => removeField(field.id)} data-testid={`nbsb-remove-field-${field.id}`} icon={<span>✕</span>} />
                </div>
                <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 170 }}>
                    <Select fluid label="Type" value={field.type} options={FIELD_TYPES}
                      onChange={(e) => updateField(field.id, { type: e.target.value })}
                      data-testid={`nbsb-field-type-${field.id}`} />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <Select fluid label="Width" value={field.width ?? 'half'} options={WIDTHS}
                      onChange={(e) => updateField(field.id, { width: e.target.value as 'half' | 'full' })}
                      data-testid={`nbsb-field-width-${field.id}`} />
                  </div>
                  <div style={{ paddingBottom: 2 }}>
                    <Checkbox label="Required" checked={!!field.required}
                      onChange={(e) => updateField(field.id, { required: e.target.checked })}
                      data-testid={`nbsb-field-required-${field.id}`} />
                  </div>
                  <div style={{ paddingBottom: 2 }}>
                    <Checkbox label="Read only" checked={!!field.readOnly}
                      onChange={(e) => updateField(field.id, { readOnly: e.target.checked })}
                      data-testid={`nbsb-field-readonly-${field.id}`} />
                  </div>
                </div>
                {field.readOnly && defaultMode(field) === 'none' && (
                  <div style={{ color: 'var(--color-secondary-text)', fontSize: 'var(--font-size-body-03)' }}
                    data-testid={`nbsb-field-readonly-hint-${field.id}`}>
                    Read-only fields need a default value — set one below so the client sees a value.
                  </div>
                )}
                <div style={{ display: 'flex', gap: 'var(--spacing-md)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 170 }}>
                    <Select fluid label="Default value" value={defaultMode(field)}
                      options={catalogue?.groups?.length ? DEFAULT_MODES : DEFAULT_MODES.filter((m) => m.value !== 'reference')}
                      onChange={(e) => setDefaultMode(field, e.target.value as DefaultMode)}
                      data-testid={`nbsb-field-default-mode-${field.id}`} />
                  </div>
                  {defaultMode(field) === 'constant' && (
                    <div style={{ flex: 1, minWidth: 160 }}>
                      <TextInput fluid label="Constant" value={field.default ?? ''}
                        onChange={(e) => updateField(field.id, { default: e.target.value })}
                        data-testid={`nbsb-field-default-const-${field.id}`} />
                    </div>
                  )}
                  {defaultMode(field) === 'reference' && (
                    <div style={{ flex: 1, minWidth: 220 }}>
                      <Select fluid label="System field" value={field.prefillFrom ?? ''}
                        options={referenceOptions(catalogue)}
                        onChange={(e) => updateField(field.id, { prefillFrom: e.target.value })}
                        data-testid={`nbsb-field-default-ref-${field.id}`} />
                    </div>
                  )}
                </div>
                {NEEDS_OPTIONS.has(field.type) && (
                  <TextArea fluid rows={3} label="Options (one per line)"
                    value={optionDrafts[field.id] ?? (field.options ?? []).join('\n')}
                    onChange={(e) => setOptionDrafts((d) => ({ ...d, [field.id]: e.target.value }))}
                    onBlur={() => {
                      const draft = optionDrafts[field.id];
                      clearDraft(setOptionDrafts, field.id);
                      if (draft !== undefined) {
                        updateField(field.id, {
                          options: draft.split('\n').map((o) => o.trim()).filter(Boolean),
                        });
                      }
                    }}
                    data-testid={`nbsb-field-options-${field.id}`} />
                )}
              </div>
            ))}
            <div>
              <Button variant="outlined" colour="blue" size="sm"
                onClick={() => addField(section.id)} data-testid={`nbsb-add-field-${section.id}`}>
                + Add field
              </Button>
            </div>
          </div>
        </div>
      ))}

      <Button variant="outlined" colour="purple" size="sm" onClick={addSection} data-testid="nbsb-add-section">
        + Add section
      </Button>
    </div>
  );
}
